import { type GECXProviderConfig } from '@/features/shop/services/ShoppingAgent/types'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { logError } from '@/features/shared/utils/logger'

type GoogleAgentApi = {
  openChat: () => void
  closeChat: () => void
  clearMessages: () => void
  refreshToken: (refreshSessionId?: boolean) => Promise<void>
}

type GECXHandlers = Pick<GECXProviderConfig, 'tokenFunc' | 'handleCartUpdate' | 'handleGetCart'>

declare global {
  interface Window {
    googleAgentSettings?: Record<string, unknown>
    googleAgent?: GoogleAgentApi
  }
}

let ready = false
let handlers: GECXHandlers | null = null
let lastStoreKey: string | null = null

function withAgent(action: (agent: GoogleAgentApi) => void): void {
  if (window.googleAgent) {
    action(window.googleAgent)
    return
  }
  window.addEventListener(
    'google-agent-loaded',
    () => {
      if (window.googleAgent) action(window.googleAgent)
    },
    { once: true },
  )
}

function init(config: GECXProviderConfig): void {
  // settings object points at this `handlers` variable instead of at the functions directly.
  // Every call to `init` overwrites `handlers`, so whichever component most recently mounted is the one the agent talks to
  handlers = {
    tokenFunc: config.tokenFunc,
    handleCartUpdate: config.handleCartUpdate,
    handleGetCart: config.handleGetCart,
  }

  if (ready) return

  window.googleAgentSettings = {
    id: config.customerId,
    cartVersion: 2,
    tokenFunc: (sessionId?: string) => handlers!.tokenFunc(sessionId),
    handleCartUpdate: (...args: Parameters<GECXHandlers['handleCartUpdate']>) =>
      handlers!.handleCartUpdate(...args),
    handleGetCart: () => handlers!.handleGetCart(),
    settings: {
      'chat-title': 'AI Shopping Assistant',
      'chat-window-position': 'hv-center',
      'disclosure-text': 'AI-powered assistant. May make mistakes.',
      'placeholder-text': 'What can I help you find?',
      'hide-entry-toggle': true,
      ...config.settings,
    },
    ...config.metadata,
  }
  ready = true
}

function open(): void {
  withAgent((agent) => agent.openChat())
}

function close(): void {
  withAgent((agent) => agent.closeChat())
}

function isReady(): boolean {
  return ready
}
//On a store change we mint a fresh store-scoped session (refreshToken(true)) and clear the now-stale conversation
function syncStore(storeParams: StoreParams): void {
  const { store_id, store_location_id, metro_id } = storeParams
  // Defer until the store is known, otherwise we would baseline against a store-less session.
  if (!store_id || !store_location_id) return

  const storeKey = `${store_id}:${store_location_id}:${metro_id}`
  if (lastStoreKey === storeKey) return

  // First store we see, so there is no earlier session to renew.
  if (lastStoreKey === null) {
    lastStoreKey = storeKey
    return
  }

  withAgent((agent) => {
    agent
      .refreshToken(true)
      .then(() => {
        lastStoreKey = storeKey
        agent.clearMessages()
      })
      .catch((err) =>
        logError('[ShoppingAgent] Failed to renew session on store switch', {
          err,
        }),
      )
  })
}

export const gecxProvider = {
  init,
  open,
  close,
  isReady,
  syncStore,
}
